import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Link
} from '@mui/material';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { TwoFactorAuthValidationFormType } from '../../../types/types';
import { useAuth } from '../../../contexts/AuthContext';
import { useUser } from '../../../contexts/UserContext';
import { setUserId, trackEvent } from '../../../utils/googleAnalytics';
import { useNetwork } from '../../../utils/network';
import { useShowSnackbar } from '../../../utils/snackbar';
import {
  RECOVERY_CODE_PLACEHOLDER,
  normalizeRecoveryCode,
  rememberRecoveryCodeSignIn
} from '../../../utils/recoveryCodes';
import TurnstileWidget from '../../Home/DynamicHomepage/components/CaptchaWidget/CaptchaWidget';
import { AuthCard } from '../../../components/AuthShell';

type TwoFactorAuthValidationFormProps = {
  rememberUser: boolean;
  preAccessToken: string;
};

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

export const TwoFactorAuthValidationForm = ({
  rememberUser,
  preAccessToken
}: TwoFactorAuthValidationFormProps) => {
  const { markSignedIn } = useAuth();
  const { setUser } = useUser();
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();

  const { handleSubmit, control, setValue } =
    useForm<TwoFactorAuthValidationFormType>();

  const [loading, setLoading] = useState(false);

  // "Use a recovery code instead": the same field and the same request, taking the
  // recovery code's shape (xxxxx-xxxxx-xxxxx) in place of the authenticator's six digits.
  const [recoveryMode, setRecoveryMode] = useState(false);

  const toggleRecoveryMode = () => {
    setRecoveryMode((on) => !on);
    setValue('token', '');
  };

  // ✅ captcha + rate limit states
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(
    null
  );

  // If token expires / user edits, clear rate message
  useEffect(() => {
    setRetryAfterSeconds(null);
  }, [captchaToken]);

  const onSubmit: SubmitHandler<TwoFactorAuthValidationFormType> = async (
    data
  ) => {
    const token = String(data.token || '').trim();

    if (!token) {
      showSnackbar('Enter your 2FA code.', 'error');
      return;
    }

    // Sent in the one shape the server reads; a typo that cannot be a code is caught
    // here rather than spending a sign-in attempt.
    const twoFactorToken = recoveryMode ? normalizeRecoveryCode(token) : token;
    if (!twoFactorToken) {
      showSnackbar(
        `That isn't a recovery code. They look like ${RECOVERY_CODE_PLACEHOLDER}.`,
        'error'
      );
      return;
    }

    if (!preAccessToken) {
      showSnackbar('Missing login session. Please login again.', 'error');
      return;
    }

    if (captchaRequired && !captchaToken) {
      showSnackbar('Please complete the captcha to continue.', 'error');
      return;
    }

    setLoading(true);

    try {
      setRetryAfterSeconds(null);

      const result = await post(
        '/auth/otp/validate',
        {
          rememberUser,
          twoFactorToken,
          preAccessToken,
          ...(captchaRequired ? { captchaToken } : {})
        },
        false
      );

      // success: clear captcha state
      setCaptchaRequired(false);
      setCaptchaToken('');

      // Cookies are set server-side; just record the session.
      markSignedIn();
      // In on a recovery code: the portal says so on arrival, with how many are left
      // (`recoveryCodesRemaining`; the design doc's draft calls it `remaining`). After
      // markSignedIn, which clears the last sign-in's notice.
      if (result.usedRecoveryCode) {
        rememberRecoveryCodeSignIn(result.recoveryCodesRemaining ?? result.remaining);
      }
      setUser(result.user);
      setUserId(result.user.id);
      trackEvent('login', { method: 'password_2fa' });
    } catch (e: any) {
      // ✅ matches your working pattern
      const status = e?.status;
      const dataResp = e?.data || {};

      // rate limit
      if (status === 429) {
        setRetryAfterSeconds(Number(dataResp?.retryAfterSeconds || 0) || null);
        if (dataResp?.requiresCaptcha) {
          setCaptchaRequired(true);
          setCaptchaToken('');
        }
        showSnackbar(
          dataResp?.message || 'Too many attempts. Try again shortly.',
          'error'
        );
        return;
      }

      // captcha required / failed
      if (status === 403 && dataResp?.requiresCaptcha) {
        setCaptchaRequired(true);
        setCaptchaToken('');
        showSnackbar(dataResp?.message || 'Captcha required', 'error');
        return;
      }

      showSnackbar(
        dataResp?.message || 'Two Factor Authentication failed',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h4" sx={{ textAlign: 'center' }}>
          Two-Factor Authentication
        </Typography>

        <Box sx={{ mt: 3, width: '100%' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="token"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  // A new key per mode remounts the field, so autoFocus lands in it again.
                  key={recoveryMode ? 'recovery' : 'totp'}
                  margin="normal"
                  required
                  fullWidth
                  label={recoveryMode ? 'Recovery code' : 'Enter 2FA Code'}
                  id="token"
                  autoFocus
                  {...(recoveryMode
                    ? {
                        placeholder: RECOVERY_CODE_PLACEHOLDER,
                        helperText: 'One of the codes you saved when you turned on 2FA',
                        inputProps: {
                          autoCapitalize: 'none',
                          autoCorrect: 'off',
                          autoComplete: 'off',
                          spellCheck: false,
                          // 16px or iOS zooms the page into the field
                          style: { fontSize: 16 }
                        }
                      }
                    : {})}
                  {...field}
                />
              )}
            />

            {/* ✅ CAPTCHA only when server requires it */}
            {captchaRequired && (
              <Box
                sx={{
                  mt: 1.2,
                  p: 1.6,
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.10)',
                  background: 'rgba(0,0,0,0.03)'
                }}
              >
                <Typography sx={{ fontWeight: 800, textAlign: 'center' }}>
                  Please verify you’re human
                </Typography>

                {!TURNSTILE_SITE_KEY ? (
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: 800,
                      color: 'rgba(255,90,90,0.95)',
                      textAlign: 'center'
                    }}
                  >
                    Missing REACT_APP_TURNSTILE_SITE_KEY
                  </Typography>
                ) : (
                  <TurnstileWidget
                    siteKey={TURNSTILE_SITE_KEY}
                    onToken={(t) => setCaptchaToken(t)}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                )}

                {retryAfterSeconds != null && retryAfterSeconds > 0 && (
                  <Typography
                    sx={{
                      mt: 1.2,
                      fontWeight: 700,
                      textAlign: 'center',
                      opacity: 0.75
                    }}
                  >
                    Try again in ~{retryAfterSeconds}s
                  </Typography>
                )}
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || (captchaRequired && !captchaToken)}
              sx={{ mt: 3, mb: 2 }}
              startIcon={loading ? <CircularProgress size={18} /> : undefined}
            >
              {loading ? 'Verifying…' : 'Submit'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={toggleRecoveryMode}
              >
                {recoveryMode
                  ? 'Use your authenticator app instead'
                  : 'Use a recovery code instead'}
              </Link>
            </Box>
            {/* Neither? The reset asks for the password again plus what they know
                about the account, waits 72 hours, and is decided by an admin. */}
            <Box sx={{ textAlign: 'center', mt: 1.25 }}>
              <Link
                component={ReactRouterLink}
                to="/account/2fa-reset"
                variant="body2"
                sx={{ opacity: 0.8 }}
              >
                Lost your authenticator and your recovery codes?
              </Link>
            </Box>
          </form>
        </Box>
      </Box>
    </AuthCard>
  );
};
